/**
 * Questions the assistant asks for one role: add, remove, reorder (up/down
 * buttons — reliable on phones), key, prompt, type, choices, required.
 */
import { useId } from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import type { QuestionType, ScreeningQuestion } from './types';
import type { QuestionErrors } from './validate';

const input =
  'mt-1 block min-h-10 w-full rounded-md border border-gray-300 bg-white px-2.5 text-sm text-gray-900 aria-[invalid=true]:border-danger';
const iconBtn = 'flex h-10 w-10 items-center justify-center rounded-md text-gray-500 hover:bg-page hover:text-gray-800 disabled:opacity-30';

const TYPE_LABEL: Record<QuestionType, string> = { text: 'Free text', number: 'Number', boolean: 'Yes / no', choice: 'One of a list' };

function Err({ id, text }: { id: string; text: string | undefined }) {
  if (!text) return null;
  return <span id={id} role="alert" className="mt-1 block text-[12px] text-danger">{text}</span>;
}

function QuestionRow({
  q, index, count, errors, onChange, onRemove, onMove,
}: {
  q: ScreeningQuestion; index: number; count: number; errors: QuestionErrors | undefined;
  onChange: (next: ScreeningQuestion) => void; onRemove: () => void; onMove: (dir: -1 | 1) => void;
}) {
  const id = useId();
  const name = q.prompt.trim() || q.key || `Question ${index + 1}`;
  const set = (patch: Partial<ScreeningQuestion>) => onChange({ ...q, ...patch });
  return (
    <li className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4" aria-label={name} data-testid={`question-${index}`}>
      <div className="flex items-start gap-2">
        <span className="mt-2 w-6 shrink-0 text-[13px] font-semibold text-muted tabular-nums" aria-hidden>{index + 1}.</span>
        <div className="grid min-w-0 flex-1 gap-3 sm:grid-cols-[1fr_auto]">
          <div className="text-[13px]">
            <label htmlFor={`${id}-prompt`} className="font-medium text-gray-800">Question</label>
            <input id={`${id}-prompt`} value={q.prompt} onChange={(e) => set({ prompt: e.target.value })} placeholder="How many years have you worked as a technician?" aria-invalid={errors?.prompt ? true : undefined} aria-describedby={errors?.prompt ? `${id}-prompt-err` : undefined} className={input} />
            <Err id={`${id}-prompt-err`} text={errors?.prompt} />
          </div>
          <div className="text-[13px] sm:w-44">
            <label htmlFor={`${id}-key`} className="font-medium text-gray-800">Key</label>
            <input id={`${id}-key`} value={q.key} onChange={(e) => set({ key: e.target.value })} spellCheck={false} autoCapitalize="off" aria-invalid={errors?.key ? true : undefined} aria-describedby={errors?.key ? `${id}-key-err` : `${id}-key-hint`} className={`${input} mono`} />
            {errors?.key ? <Err id={`${id}-key-err`} text={errors.key} /> : <span id={`${id}-key-hint`} className="mt-1 block text-[12px] text-muted">Used in rules; e.g. years_experience.</span>}
          </div>
          <div className="text-[13px]">
            <label htmlFor={`${id}-type`} className="font-medium text-gray-800">Answer type</label>
            <select id={`${id}-type`} value={q.type} onChange={(e) => { const type = e.target.value as QuestionType; set({ type, choices: type === 'choice' ? q.choices ?? [] : undefined }); }} className={input}>
              {(Object.keys(TYPE_LABEL) as QuestionType[]).map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
            </select>
          </div>
          <label className="flex min-h-10 items-center gap-2 self-end text-[13px] text-gray-800">
            <input type="checkbox" checked={q.required} onChange={(e) => set({ required: e.target.checked })} className="h-4 w-4 accent-navy" />
            Required — a missing answer sends the candidate to review
          </label>
          {q.type === 'choice' && (
            <div className="text-[13px] sm:col-span-2">
              <label htmlFor={`${id}-choices`} className="font-medium text-gray-800">Choices</label>
              <input id={`${id}-choices`} value={(q.choices ?? []).join(', ')} onChange={(e) => set({ choices: e.target.value.split(',').map((s) => s.trimStart()) })} placeholder="Days, Nights, Weekends" aria-invalid={errors?.choices ? true : undefined} aria-describedby={errors?.choices ? `${id}-choices-err` : undefined} className={input} />
              <Err id={`${id}-choices-err`} text={errors?.choices} />
            </div>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-center">
          <button type="button" onClick={() => onMove(-1)} disabled={index === 0} aria-label={`Move question ${index + 1} up`} className={iconBtn}><ArrowUp size={15} strokeWidth={2} aria-hidden /></button>
          <button type="button" onClick={() => onMove(1)} disabled={index === count - 1} aria-label={`Move question ${index + 1} down`} className={iconBtn}><ArrowDown size={15} strokeWidth={2} aria-hidden /></button>
          <button type="button" onClick={onRemove} aria-label={`Remove question ${index + 1}`} className={`${iconBtn} text-danger hover:bg-danger-bg hover:text-danger`}><Trash2 size={15} strokeWidth={2} aria-hidden /></button>
        </div>
      </div>
    </li>
  );
}

export function QuestionsEditor({
  questions, errors, onChange,
}: {
  questions: ScreeningQuestion[]; errors: Record<number, QuestionErrors>; onChange: (next: ScreeningQuestion[]) => void;
}) {
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= questions.length) return;
    const next = [...questions];
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  };
  return (
    <section aria-labelledby="questions-h" className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 id="questions-h" className="text-[15px] font-semibold text-gray-900">Questions</h2>
          <p className="mt-0.5 text-[13px] text-muted">Asked in this order. The assistant skips anything already answered on the application form.</p>
        </div>
        <button type="button" onClick={() => onChange([...questions, { key: '', prompt: '', type: 'text', required: false }])} className="inline-flex min-h-10 items-center gap-1.5 rounded-md border border-gray-200 px-3 text-[13px] font-medium text-gray-800 hover:bg-page">
          <Plus size={14} strokeWidth={2} aria-hidden /> Add question
        </button>
      </div>
      {questions.length === 0 ? (
        <p className="mt-3 text-[13px] text-muted">No questions yet. Add the first one to give the assistant something to ask.</p>
      ) : (
        <ol className="mt-3 space-y-3">
          {questions.map((q, i) => (
            <QuestionRow key={i} q={q} index={i} count={questions.length} errors={errors[i]} onChange={(next) => onChange(questions.map((x, j) => (j === i ? next : x)))} onRemove={() => onChange(questions.filter((_, j) => j !== i))} onMove={(dir) => move(i, dir)} />
          ))}
        </ol>
      )}
    </section>
  );
}
